// Analytics utility functions for fetching dynamic data

import { asAbortSignal } from '../abortSignal';

const API_BASE_URL = '/api';

function isAbortLike(error) {
  if (!error) return false;
  if (error.name === 'AbortError' || error.name === 'CanceledError' || error.code === 'ERR_CANCELED') {
    return true;
  }
  // Navigation/unmount often surfaces as TypeError: Failed to fetch (not AbortError).
  return error instanceof TypeError && /Failed to fetch/i.test(error.message || '');
}

function asAbortError(error) {
  if (error?.name === 'AbortError') return error;
  return Object.assign(error || new Error('Aborted'), { name: 'AbortError' });
}

async function fetchJson(path, options = {}) {
  try {
    const response = await fetch(`${API_BASE_URL}${path}`, {
      method: 'GET',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
      },
      signal: asAbortSignal(options.signal),
    });

    if (!response.ok) {
      throw new Error(options.errorMessage || 'Failed to fetch analytics data');
    }

    return response.json();
  } catch (error) {
    if (isAbortLike(error)) {
      throw asAbortError(error);
    }
    console.error(options.logLabel || 'Error fetching analytics:', error);
    throw error;
  }
}

// Fetch user analytics trends
export const fetchUserAnalytics = async (options = {}) =>
  fetchJson('/analytics/trends', {
    ...options,
    errorMessage: 'Failed to fetch analytics data',
    logLabel: 'Error fetching user analytics:',
  });

// Fetch admin analytics trends
export const fetchAdminAnalytics = async (companyId = null, options = {}) => {
  const query = companyId ? `?company_id=${encodeURIComponent(companyId)}` : '';
  return fetchJson(`/analytics/admin-trends${query}`, {
    ...options,
    errorMessage: 'Failed to fetch admin analytics data',
    logLabel: 'Error fetching admin analytics:',
  });
};

// Fetch user stats
export const fetchUserStats = async (options = {}) =>
  fetchJson('/stats', {
    ...options,
    errorMessage: 'Failed to fetch user stats',
    logLabel: 'Error fetching user stats:',
  });

// Fetch vendor analytics data
export const fetchVendorAnalytics = async (options = {}) =>
  fetchJson('/vendor/stats', {
    ...options,
    errorMessage: 'Failed to fetch vendor analytics data',
    logLabel: 'Error fetching vendor analytics:',
  });

// Fetch vendor trends data
export const fetchVendorTrends = async (options = {}) =>
  fetchJson('/vendor/trends', {
    ...options,
    errorMessage: 'Failed to fetch vendor trends data',
    logLabel: 'Error fetching vendor trends:',
  });

// Helper function to format trend data for StatsCard
export const formatTrendData = (trendData, type) => {
  if (!trendData || !trendData[type]) {
    return null;
  }

  const trend = trendData[type];
  return {
    data: trend.data,
    period: trend.period,
    change: trend.change,
    changeType: trend.changeType
  };
};
